import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import LinearTheoryDemo from "../lessons/Lesson_2_2_1_Liniowe/Lesson1_TeoriaPrzyklady";
import LinearTasksDemo from "../lessons/Lesson_2_2_1_Liniowe/Lesson2_Tasks";
import QuadraticGeneralTheoryDemo from "../lessons/Lesson_3_3_1_Ogolna/Lesson1_TeoriaPrzyklady";
import QuadraticGeneralTasksDemo from "../lessons/Lesson_3_3_1_Ogolna/Lesson2_Tasks";
import CircleCanonicalTheoryDemo from "../lessons/Lesson_7_3_1_Kanoniczna/Lesson1_TeoriaPrzyklady";
import CircleCanonicalTasksDemo from "../lessons/Lesson_7_3_1_Kanoniczna/Lesson2_Tasks";
import UniversalQuizRunner from "../components/QuizRunner/UniversalQuizRunner";
import { quizTypeRegistry } from "../components/QuizRunner/quizTypeRegistry";
import {
  combinedRegistry,
  paginateExamTasks,
} from "../components/MaturaExamRunner/MaturaExamCore";
import MaturaExamPager from "../components/MaturaExamRunner/MaturaExamPager";
import EdgeDrawer from "../components/ui/EdgeDrawer";
import ExamCalculator from "../components/ExamCalculator";
import FormulaSheet from "../components/FormulaSheet";
import HorizontalScroll from "../components/ui/HorizontalScroll";
import TaskInlineWhiteboard from "../components/TaskRunner/TaskInlineWhiteboard";
import WhiteboardCanvas from "../whiteboard/components/WhiteboardCanvas";
import {
  ImageIcon,
  TaskIcon,
} from "../whiteboard/components/WhiteboardControlPrimitives";
import { generateTasks as generateLinearTasks } from "../generators/dzial2_algebra/temat2_rownania/podtemat1_liniowe";
import { generateTasks as generateQuadraticGeneralTasks } from "../generators/dzial3_funkcje/temat3_kwadratowa/podtemat1_ogolna";
import { generateTasks as generateCircleCanonicalTasks } from "../generators/dzial7_geometria_analityczna/temat3_okrag/podtemat1_kanoniczna";
import { generatePagedTasks as generateOfficialAugust2025Matura } from "../matura/official/CKE_P_2025_SIE";
import "./GrantInvestorTourPage.css";

const STORAGE_KEY = "matematron.publicTour.v2";
const WZORY_URL =
  process.env.PUBLIC_URL + "/assets/wybrane_wzory_matematyczne_EM2023.pdf";
const CALCULATOR_DRAWER_WIDTH = 420;
const FORMULA_SHEET_DRAWER_WIDTH = 740;

const lessonDemoTopics = [
  {
    id: "linear",
    sectionTitle: "2_2_1",
    label: "Równania liniowe",
    eyebrow: "Algebra",
    title: "Równania liniowe",
    Theory: LinearTheoryDemo,
    Tasks: LinearTasksDemo,
    generateTasks: generateLinearTasks,
  },
  {
    id: "quadratic-general",
    sectionTitle: "3_3_1",
    label: "Funkcja kwadratowa",
    eyebrow: "Funkcje",
    title: "Postać ogólna funkcji kwadratowej",
    Theory: QuadraticGeneralTheoryDemo,
    Tasks: QuadraticGeneralTasksDemo,
    generateTasks: generateQuadraticGeneralTasks,
  },
  {
    id: "circle-canonical",
    sectionTitle: "7_3_1",
    label: "Okrąg",
    eyebrow: "Geometria analityczna",
    title: "Okrąg w postaci kanonicznej",
    Theory: CircleCanonicalTheoryDemo,
    Tasks: CircleCanonicalTasksDemo,
    generateTasks: generateCircleCanonicalTasks,
  },
];

const publicSteps = [
  {
    id: "intro",
    label: "Mapa platformy",
    title: "Jak działa Matematron",
    lead:
      "Zobacz trzy główne segmenty produktu: tablicę do wspólnej pracy, lekcje z quizem i postępem oraz indywidualne arkusze egzaminacyjne.",
    hint: "To jest demonstracja poglądowa. Nie musisz zakładać konta, żeby zobaczyć najważniejsze elementy.",
  },
  {
    id: "board",
    label: "Tablica",
    title: "Prawdziwa tablica w trybie demo",
    lead:
      "Tablica jest osobnym narzędziem do pracy nad zadaniami: rysowanie, wykresy, zadania, kalkulator, karta wzorów i praca na kilku stronach.",
    hint: "Otwórz tablicę demo. Działa na tym samym komponencie co normalna tablica, ale bez zapisu na serwerze.",
  },
  {
    id: "lesson",
    label: "Lekcja",
    title: "Teoria, przykłady i ćwiczenia w jednym temacie",
    lead:
      "Zamiast pustej makiety podpinamy fragment realnej lekcji. W jednym miejscu jest teoria, ćwiczenia, interaktywny quiz i lokalny zapis postępu.",
    hint: "Przełącz zakładki w podglądzie: teoria, ćwiczenia, quiz i postęp.",
  },
  {
    id: "matura",
    label: "Matura",
    title: "Indywidualny arkusz egzaminacyjny",
    lead:
      "Uczeń może pracować z interaktywnym arkuszem maturalnym: rozwiązywać zadania, sprawdzać wynik i wracać do analizy. Docelowo ten sam kierunek obejmie egzamin ósmoklasisty.",
    hint: "To krok poglądowy. Pełny moduł maturalny będziemy dopracowywać w kolejnej części demonstracji.",
  },
];

const investorStep = {
  id: "plan",
  label: "Plan",
  title: "Plan rozwoju dla partnera lub inwestora",
  lead:
    "W wersji inwestorskiej ten krok pokazuje, co już działa, co wymaga dopracowania i jak środki przyspieszą testy, treści oraz pozyskanie pierwszych grup użytkowników.",
  hint: "Ten krok ukrywamy w zwykłej demonstracji produktowej.",
};

function makeTourStroke(id, points, color = "#f59e0b", size = 4) {
  return {
    id,
    type: "stroke",
    tool: "pen",
    color,
    width: size,
    page: 0,
    points,
  };
}

function rotateTourPoint(point, angleDeg, origin = { x: 640, y: 430 }) {
  const angle = (angleDeg * Math.PI) / 180;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const dx = point.x - origin.x;
  const dy = point.y - origin.y;
  return {
    x: Math.round(origin.x + dx * cos - dy * sin),
    y: Math.round(origin.y + dx * sin + dy * cos),
  };
}

function makeHandwrittenTourStroke(id, points, color = "#f8fafc", size = 9) {
  return makeTourStroke(id, points.map((point) => rotateTourPoint(point, -8)), color, size);
}

const TOUR_BOARD_PREVIEW_STATE = {
  version: 1,
  pageCount: 1,
  elements: [
    makeHandwrittenTourStroke(
      "tour-letter-p",
      [
        { x: 260, y: 500 },
        { x: 260, y: 345 },
        { x: 265, y: 395 },
        { x: 292, y: 360 },
        { x: 330, y: 370 },
        { x: 333, y: 420 },
        { x: 304, y: 445 },
        { x: 268, y: 430 },
      ],
      "#eaf6ff"
    ),
    makeHandwrittenTourStroke(
      "tour-letter-r",
      [
        { x: 360, y: 455 },
        { x: 360, y: 372 },
        { x: 365, y: 395 },
        { x: 386, y: 370 },
        { x: 412, y: 378 },
      ],
      "#eaf6ff"
    ),
    makeHandwrittenTourStroke(
      "tour-letter-a",
      [
        { x: 455, y: 388 },
        { x: 420, y: 394 },
        { x: 408, y: 430 },
        { x: 426, y: 462 },
        { x: 462, y: 454 },
        { x: 476, y: 412 },
        { x: 474, y: 455 },
        { x: 496, y: 456 },
      ],
      "#eaf6ff"
    ),
    makeHandwrittenTourStroke(
      "tour-letter-c",
      [
        { x: 560, y: 392 },
        { x: 520, y: 382 },
        { x: 492, y: 410 },
        { x: 496, y: 448 },
        { x: 532, y: 464 },
        { x: 565, y: 444 },
      ],
      "#eaf6ff"
    ),
    makeHandwrittenTourStroke(
      "tour-letter-u",
      [
        { x: 592, y: 382 },
        { x: 592, y: 438 },
        { x: 606, y: 464 },
        { x: 642, y: 456 },
        { x: 650, y: 390 },
      ],
      "#eaf6ff"
    ),
    makeHandwrittenTourStroke(
      "tour-letter-j",
      [
        { x: 698, y: 382 },
        { x: 696, y: 472 },
        { x: 674, y: 512 },
        { x: 638, y: 504 },
      ],
      "#eaf6ff"
    ),
    makeHandwrittenTourStroke("tour-dot-1", [{ x: 735, y: 456 }, { x: 736, y: 457 }], "#eaf6ff", 12),
    makeHandwrittenTourStroke(
      "tour-letter-p-2",
      [
        { x: 768, y: 505 },
        { x: 768, y: 384 },
        { x: 772, y: 405 },
        { x: 800, y: 378 },
        { x: 834, y: 390 },
        { x: 832, y: 438 },
        { x: 802, y: 462 },
        { x: 772, y: 448 },
      ],
      "#eaf6ff"
    ),
    makeHandwrittenTourStroke(
      "tour-letter-l",
      [
        { x: 870, y: 345 },
        { x: 870, y: 452 },
        { x: 890, y: 458 },
      ],
      "#eaf6ff"
    ),
    makeHandwrittenTourStroke(
      "tour-blue-swoosh",
      [
        { x: 250, y: 548 },
        { x: 360, y: 568 },
        { x: 525, y: 575 },
        { x: 685, y: 568 },
        { x: 900, y: 540 },
      ],
      "#39d9f5",
      5
    ),
  ],
};

const TOUR_BOARD_EXTRA_BUTTON_STYLE = {
  color: "var(--color-text)",
  borderColor: "var(--color-text)",
};

function TourGraphIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 20V4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M4 20h16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path
        d="M6 16c2.2-5.8 4.3-7.8 6.4-6 1.4 1.2 2.3 3.8 5.6-2"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M8 12h10M12 6v12" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" opacity="0.45" />
    </svg>
  );
}

function loadTourState() {
  if (typeof window === "undefined") return {};
  try {
    return JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
}

function saveTourState(next) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
}

function useTourStorage() {
  const [state, setState] = useState(() => ({
    lessonTopic: "linear",
    lessonTab: "theory",
    lessonQuizSnapshots: [],
    maturaPage: 0,
    maturaAnswers: {},
    maturaChecked: false,
    ...loadTourState(),
  }));

  useEffect(() => {
    saveTourState(state);
  }, [state]);

  const patch = (updates) => setState((current) => ({ ...current, ...updates }));
  return [state, patch];
}

function getStepFromSearch(search, steps) {
  const params = new URLSearchParams(search || "");
  const requested = params.get("step");
  const index = steps.findIndex((step) => step.id === requested);
  return index >= 0 ? index : 0;
}

function IntroPreview() {
  return (
    <div className="tour-intro-panel">
      <div className="tour-intro-panel__copy">
        <span>O platformie</span>
        <h2>Matematron: platforma, która tworzy przestrzeń do nauki matematyki</h2>
        <p>
          Matematron łączy samodzielną naukę, wspólne rozwiązywanie zadań i prowadzenie korepetycji online.
          Produkt jest rozwijany jako jedno miejsce dla ucznia, korepetytora i osoby przygotowującej się do egzaminu.
        </p>
        <p>
          Ta demonstracja pokazuje trzy główne segmenty: tablicę, lekcje oraz interaktywne arkusze egzaminacyjne.
          Matura jest już częścią podglądu, a egzamin ósmoklasisty jest kolejnym obszarem w produkcji.
        </p>
      </div>
      <div className="tour-intro-map" aria-label="Główne segmenty platformy">
        {[
          ["Tablica", "wspólna praca nad zadaniem", "uczniowie, korepetytorzy, grupy"],
          ["Lekcje", "teoria, ćwiczenia, interaktywny quiz i postęp", "uczniowie uczący się krok po kroku"],
          ["Egzaminy", "indywidualne arkusze z wynikiem i analizą", "maturzyści oraz docelowo ósmoklasiści"],
        ].map(([title, connector, audience]) => (
          <div className="tour-intro-map__row" key={title}>
            <strong>{title}</strong>
            <span>{connector}</span>
            <p>{audience}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function BoardPreview() {
  const navigate = useNavigate();
  const inlineRef = useRef(null);
  const [inlineBoardHeight, setInlineBoardHeight] = useState(620);
  const extraSideButtonDefs = useMemo(
    () => [
      {
        key: "tour-add-image",
        label: <ImageIcon />,
        title: "Dodaj zdjecie",
        style: TOUR_BOARD_EXTRA_BUTTON_STYLE,
        onClick: () => navigate("/demo/tablica?tutorial=1&fullscreen=1&focus=image"),
      },
      {
        key: "tour-add-graph",
        label: <TourGraphIcon />,
        title: "Dodaj wykres",
        style: TOUR_BOARD_EXTRA_BUTTON_STYLE,
        onClick: () => navigate("/demo/tablica?tutorial=1&fullscreen=1&focus=graph"),
      },
      {
        key: "tour-add-task",
        label: <TaskIcon />,
        title: "Dodaj zadanie z platformy",
        style: TOUR_BOARD_EXTRA_BUTTON_STYLE,
        onClick: () => navigate("/demo/tablica?tutorial=1&fullscreen=1&focus=task"),
      },
    ],
    [navigate]
  );

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const node = inlineRef.current;
    if (!node) return undefined;
    let rafId = 0;

    const measure = () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      rafId = window.requestAnimationFrame(() => {
        rafId = 0;
        const currentNode = inlineRef.current;
        if (!currentNode) return;
        const styles = window.getComputedStyle(currentNode);
        const paddingY = parseFloat(styles.paddingTop || "0") + parseFloat(styles.paddingBottom || "0");
        const inlineHeight = currentNode.clientHeight || 0;
        const controlsReserve = 96;
        const nextHeight = Math.max(
          240,
          Math.min(860, Math.floor(inlineHeight - paddingY - controlsReserve))
        );
        setInlineBoardHeight((current) => (Math.abs(current - nextHeight) > 8 ? nextHeight : current));
      });
    };

    measure();
    window.addEventListener("resize", measure);

    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      window.removeEventListener("resize", measure);
    };
  }, []);

  return (
    <div className="tour-board-panel">
      <div className="tour-board-inline" ref={inlineRef}>
        <TaskInlineWhiteboard
          defaultOpen
          hideToggle
          singleOpen={false}
          openGroup="tour-board-preview"
          initialBoardState={TOUR_BOARD_PREVIEW_STATE}
          extraSideButtonDefs={extraSideButtonDefs}
          maxInlineBoardHeight={inlineBoardHeight}
          preferFixedWidthOverride
          bottomLeftTopControls={
            <Link to="/demo/tablica?tutorial=1&fullscreen=1" className="btn-primary tour-link-button">
              Samouczek
            </Link>
          }
        />
      </div>
    </div>
  );
}

function TourUtilityTools({ className = "" }) {
  const [calcOpen, setCalcOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);

  return (
    <div className={className}>
      <div className="tour-utility-tools">
        <button type="button" className="btn-secondary" onClick={() => setCalcOpen(true)}>
          Kalkulator
        </button>
        <button type="button" className="btn-secondary" onClick={() => setSheetOpen(true)}>
          Karta wzorów
        </button>
      </div>

      <EdgeDrawer
        side="left"
        isOpen={calcOpen}
        onOpenChange={setCalcOpen}
        width={CALCULATOR_DRAWER_WIDTH}
        title="Kalkulator maturalny"
        showHandle={false}
        offsetTop={0}
        background="linear-gradient(180deg, rgba(255,153,0,0.36) 0%, rgba(255,190,70,0.36) 100%)"
        contentMaxWidth={380}
        zIndex={sheetOpen ? 10002 : 1000}
        showBackdrop={false}
        headerRight={
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setSheetOpen(true)}
            style={{ fontSize: 14, padding: "2px 10px", lineHeight: 1, borderRadius: 10 }}
            title="Otwórz kartę wzorów"
          >
            Karta wzorów
          </button>
        }
      >
        {calcOpen ? <ExamCalculator embedded /> : null}
      </EdgeDrawer>

      <FormulaSheet
        isOpen={sheetOpen}
        onOpenChange={setSheetOpen}
        width={FORMULA_SHEET_DRAWER_WIDTH}
        pdfPath={WZORY_URL}
        showBackdrop={false}
        headerRightExtra={
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setCalcOpen(true)}
            style={{ fontSize: 14, padding: "2px 10px", lineHeight: 1, borderRadius: 10 }}
            title="Otwórz kalkulator"
          >
            Kalkulator
          </button>
        }
      />
    </div>
  );
}

function LessonPreview({ state, patch }) {
  const active = state.lessonTab || "theory";
  const activeTopicId = state.lessonTopic || lessonDemoTopics[0].id;
  const activeTopic = lessonDemoTopics.find((topic) => topic.id === activeTopicId) || lessonDemoTopics[0];
  const TheoryComponent = activeTopic.Theory;
  const TasksComponent = activeTopic.Tasks;
  const quizTasks = useMemo(() => activeTopic.generateTasks(), [activeTopic]);
  const moduleRef = useRef(null);
  const tabs = [
    ["theory", "Teoria"],
    ["tasks", "Ćwiczenia"],
    ["quiz", "Quiz"],
    ["progress", "Postęp"],
  ];
  const topicSnapshots = (state.lessonQuizSnapshots || []).filter((snapshot) => (
    snapshot.topicId ? snapshot.topicId === activeTopic.id : activeTopic.id === "linear"
  ));

  return (
    <div className="tour-lesson-preview">
      <div className="tour-topic-selector" aria-label="Tematy lekcji demo">
        {lessonDemoTopics.map((topic) => (
          <button
            key={topic.id}
            type="button"
            className={activeTopic.id === topic.id ? "is-active" : ""}
            onClick={() => patch({ lessonTopic: topic.id, lessonTab: "theory" })}
          >
            <span>{topic.eyebrow}</span>
            <strong>{topic.label}</strong>
          </button>
        ))}
      </div>
      <div className="tour-lesson-controls">
        <div className="tour-tabs">
          {tabs.map(([id, label]) => (
            <button
              key={id}
              type="button"
              className={active === id ? "btn-primary is-active" : "btn-secondary"}
              onClick={() => patch({ lessonTab: id })}
            >
              {label}
            </button>
          ))}
        </div>
        <TourUtilityTools />
      </div>
      <div className={`tour-embedded-module tour-embedded-module--${active}`} ref={moduleRef}>
        {active === "theory" ? <TheoryComponent /> : null}
        {active === "tasks" ? <TasksComponent hideUtilityFabs /> : null}
        {active === "quiz" ? (
          <UniversalQuizRunner
            key={activeTopic.id}
            sectionTitle={activeTopic.sectionTitle}
            tasks={quizTasks}
            forceLocalMode
            hideUtilityFabs
            snapshotTitle={`${activeTopic.title} - quiz demo`}
            onLocalSnapshot={(snapshot) => {
              const id = `lesson_quiz_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
              const nextSnapshot = {
                id,
                topicId: activeTopic.id,
                topic: activeTopic.title,
                ...snapshot,
              };
              patch({
                lessonQuizSnapshots: [nextSnapshot, ...(state.lessonQuizSnapshots || [])].slice(0, 8),
              });
            }}
          />
        ) : null}
        {active === "progress" ? (
          <LessonProgressSnapshot
            topicTitle={activeTopic.title}
            snapshots={topicSnapshots}
            scrollContainerRef={moduleRef}
          />
        ) : null}
      </div>
    </div>
  );
}

function LessonProgressSnapshot({ topicTitle, snapshots, scrollContainerRef }) {
  const detailRef = useRef(null);
  const [openedSnapshotId, setOpenedSnapshotId] = useState("");
  const activeSnapshot = snapshots.find((snapshot) => String(snapshot.id) === String(openedSnapshotId)) || null;
  const bestSnapshot = snapshots.reduce((best, snapshot) => {
    if (!best) return snapshot;
    return Number(snapshot.percent || 0) > Number(best.percent || 0) ? snapshot : best;
  }, null);

  const selectSnapshot = (id) => {
    setOpenedSnapshotId(id);
  };

  useEffect(() => {
    if (!openedSnapshotId) return;
    const container = scrollContainerRef?.current;
    const detail = detailRef.current;
    if (!container || !detail) return;
    const containerBox = container.getBoundingClientRect();
    const detailBox = detail.getBoundingClientRect();
    container.scrollTo({
      top: container.scrollTop + detailBox.top - containerBox.top - 8,
      behavior: "smooth",
    });
  }, [openedSnapshotId, scrollContainerRef]);

  return (
    <div className="tour-progress-preview">
      <div className="tour-progress-head">
        <div>
          <strong>{topicTitle}</strong>
          <span>{snapshots.length ? `${snapshots.length} zapisane podejścia` : "brak zapisanych podejść"}</span>
        </div>
        {snapshots.length ? (
          <div className="tour-progress-chips">
            <ProgressChip label="Ostatni" snapshot={snapshots[0]} />
            {bestSnapshot?.id !== snapshots[0]?.id ? <ProgressChip label="Najlepszy" snapshot={bestSnapshot} /> : null}
          </div>
        ) : null}
      </div>

      {snapshots.length ? (
        <div className="tour-snapshot-list" aria-label="Zapisane podejścia quizu">
          {snapshots.map((snapshot, index) => (
            <button
              key={snapshot.id}
              type="button"
              className={String(openedSnapshotId) === String(snapshot.id) ? "tour-snapshot-row is-active" : "tour-snapshot-row"}
              onClick={() => selectSnapshot(snapshot.id)}
            >
              <span>Podejscie {snapshots.length - index}</span>
              <span>{snapshot.percent}%</span>
              <span>{formatSnapshotDate(snapshot.checkedAt)}</span>
            </button>
          ))}
        </div>
      ) : (
        <p className="tour-progress-empty">Rozwiąż quiz w tej lekcji, żeby zobaczyć lokalny zapis podejścia.</p>
      )}

      {activeSnapshot ? (
        <SnapshotAttemptViewer ref={detailRef} snapshot={activeSnapshot} />
      ) : snapshots.length ? (
        <p className="tour-progress-empty">Kliknij podejście, żeby otworzyć zapis.</p>
      ) : null}
    </div>
  );
}

function ProgressChip({ label, snapshot }) {
  if (!snapshot) return null;
  return (
    <span className="tour-progress-chip">
      {label}: {Number(snapshot.percent || 0)}% {formatSnapshotDate(snapshot.checkedAt)}
    </span>
  );
}

function formatSnapshotDate(iso) {
  if (!iso) return "";
  const date = new Date(iso);
  if (!Number.isFinite(date.getTime())) return "";
  return date.toLocaleString("pl-PL", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function renderSnapshotUserAnswer(adapter, answer, task, feedback) {
  if (adapter?.renderUserAnswer) {
    return adapter.renderUserAnswer(answer, task, feedback);
  }
  if (answer == null || answer === "") return "brak odpowiedzi";
  if (typeof answer === "object") {
    if (answer.raw) return answer.raw;
    if (answer.a?.raw || answer.b?.raw) {
      return `a = ${answer.a?.raw || "-"}, b = ${answer.b?.raw || "-"}`;
    }
    return JSON.stringify(answer);
  }
  return String(answer);
}

function renderSnapshotExplanation(adapter, task) {
  if (!adapter?.renderExplanation) return null;
  return adapter.renderExplanation({
    data: task.data,
    answer: Array.isArray(task.answer)
      ? task.answer
      : Array.isArray(task.answer?.answer)
      ? task.answer.answer
      : task.answer,
  });
}

function normalizeTourWhiteboard(source) {
  const state = source && typeof source === "object" ? source : {};
  return {
    version: Number(state.version || 1),
    elements: Array.isArray(state.elements) ? state.elements : [],
    pageCount: Math.max(1, Math.min(100, Number(state.pageCount || 1))),
    undoStack: [],
    redoStack: [],
    updatedAt: Number(state.updatedAt || Date.now()),
    updatedBy: state.updatedBy || "tour",
  };
}

const SnapshotAttemptViewer = React.forwardRef(function SnapshotAttemptViewer({ snapshot }, ref) {
  const [expanded, setExpanded] = useState({});
  const [whiteboardExpanded, setWhiteboardExpanded] = useState({});
  const tasks = Array.isArray(snapshot.tasks) ? snapshot.tasks : [];
  if (!tasks.length) return null;

  return (
    <div className="tour-snapshot-detail" ref={ref}>
      <div className="tour-snapshot-detail__header">
        <div className="tour-snapshot-titlebar">
          <TourBadge tone="orange">Podgląd zapisanego podejścia</TourBadge>
          <span>{formatSnapshotDate(snapshot.checkedAt) || "zapis lokalny"}</span>
        </div>
        <strong>{snapshot.score}/{snapshot.maxScore} pkt</strong>
      </div>
      <h3 className="tour-snapshot-title">{snapshot.snapshotTitle || "Zapis podejścia"}</h3>
      {tasks.map((task, index) => {
        const adapter = quizTypeRegistry[task.type];
        const feedback = snapshot.feedback?.[index] || {};
        const score = Number(feedback.score || 0);
        const maxScore = Number(feedback.maxScore || task.points || 1);
        const explanation = renderSnapshotExplanation(adapter, task);
        const isCorrect = !!feedback.isCorrect;
        const hasPartial = score > 0 && score < maxScore;
        const badgeTone = isCorrect ? "success" : hasPartial ? "warn" : "danger";
        const badgeText = isCorrect ? "poprawna" : hasPartial ? "częściowo poprawna" : "błędna";
        const whiteboardState = normalizeTourWhiteboard(snapshot.taskWhiteboards?.[index]);
        const hasWhiteboard = whiteboardState.elements.length > 0;
        return (
          <article key={`${task.type || "task"}-${index}`} className="tour-snapshot-task">
            <div className="tour-snapshot-task__top">
              <div>
                <strong>Zadanie {index + 1}.</strong>
                <div className="tour-snapshot-task__body">
                  {adapter?.renderTask ? (
                    <HorizontalScroll rightPadding={48}>{adapter.renderTask(task)}</HorizontalScroll>
                  ) : (
                    <p>Brak renderera dla typu: {task.type || "nieznany typ zadania"}.</p>
                  )}
                </div>
              </div>
              <div className="tour-snapshot-badges">
                <TourBadge tone={badgeTone}>{badgeText}</TourBadge>
                <TourBadge tone={score === maxScore ? "success" : score > 0 ? "warn" : "danger"}>{score}/{maxScore}</TourBadge>
              </div>
            </div>
            <div className="tour-snapshot-answer">
              <span>Twoja odpowiedz:</span>
              <strong>{renderSnapshotUserAnswer(adapter, snapshot.answers?.[index], task, feedback)}</strong>
            </div>
            {!adapter?.hideCorrectAnswerWhenUserAnswer && adapter?.renderAnswer ? (
              <div className="tour-snapshot-answer">
                <span>Poprawna odpowiedz:</span>
                <strong>{adapter.renderAnswer(task)}</strong>
              </div>
            ) : null}
            {feedback.message ? <p className="tour-snapshot-message">{feedback.message}</p> : null}
            <div className="tour-snapshot-note">
              {hasWhiteboard ? (
                <>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setWhiteboardExpanded((current) => ({ ...current, [index]: !current[index] }))}
                  >
                    {whiteboardExpanded[index] ? "Zwin notatke" : "Zobacz notatke"}
                  </button>
                  {whiteboardExpanded[index] ? (
                    <div className="tour-snapshot-whiteboard">
                      <WhiteboardCanvas
                        value={whiteboardState}
                        onChange={() => {}}
                        onAddPage={() => {}}
                        tool="select"
                        color="#f59e0b"
                        brushSize={3}
                        disabled
                        zoomPercent={100}
                        themeMode="dark"
                        boardSurface="default"
                        gridEnabled
                        allowAddPage={false}
                        maxDisplayWidth={760}
                      />
                    </div>
                  ) : null}
                </>
              ) : (
                <div className="tour-snapshot-no-note">Brak notatki.</div>
              )}
            </div>
            {explanation ? (
              <div className="tour-snapshot-explanation">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setExpanded((current) => ({ ...current, [index]: !current[index] }))}
                >
                  {expanded[index] ? "Ukryj wyjasnienie" : "Pokaz wyjasnienie"}
                </button>
                {expanded[index] ? (
                  <div className="tour-snapshot-explanation__body">
                    <HorizontalScroll rightPadding={48}>{explanation}</HorizontalScroll>
                  </div>
                ) : null}
              </div>
            ) : null}
          </article>
        );
      })}
    </div>
  );
});

function TourBadge({ children, tone = "neutral" }) {
  return <span className={`tour-snapshot-badge tour-snapshot-badge--${tone}`}>{children}</span>;
}

function MaturaPreview({ state, patch }) {
  const pages = [
    {
      title: "Strona 1",
      subtitle: "Zadania zamkniete",
      tasks: [
        { id: "m1", text: "25% liczby 240 to:", options: ["40", "60", "80"], correct: "60" },
        { id: "m2", text: "Miejsce zerowe funkcji f(x)=x-5:", options: ["-5", "0", "5"], correct: "5" },
      ],
    },
    {
      title: "Strona 2",
      subtitle: "Zadania otwarte",
      tasks: [
        { id: "m3", text: "Rozwiaz rownanie 3x - 6 = 12.", options: ["x = 2", "x = 6", "x = 18"], correct: "x = 6" },
        { id: "m4", text: "Wybierz poprawny opis: delta > 0 oznacza...", options: ["0 miejsc zerowych", "1 miejsce zerowe", "2 miejsca zerowe"], correct: "2 miejsca zerowe" },
      ],
    },
    {
      title: "Podsumowanie",
      subtitle: "Diagnoza po arkuszu",
      tasks: [],
    },
  ];
  const pageIndex = Math.max(0, Math.min(Number(state.maturaPage || 0), pages.length - 1));
  const page = pages[pageIndex];
  const answers = state.maturaAnswers || {};
  const allTasks = pages.flatMap((item) => item.tasks);
  const score = allTasks.filter((task) => answers[task.id] === task.correct).length;
  const percent = Math.round((score / Math.max(1, allTasks.length)) * 100);

  const setAnswer = (id, value) => {
    patch({ maturaAnswers: { ...answers, [id]: value } });
  };

  return (
    <div className="tour-matura-preview">
      <div className="tour-tabs">
        {pages.map((item, index) => (
          <button
            key={item.title}
            type="button"
            className={pageIndex === index ? "btn-primary is-active" : "btn-secondary"}
            onClick={() => patch({ maturaPage: index })}
          >
            {item.title}
          </button>
        ))}
      </div>
      <div className="tour-matura-paper">
        {page.tasks.length ? (
          <>
            <span>{page.subtitle}</span>
            <h3>{page.title}</h3>
            <div className="tour-matura-task-list">
              {page.tasks.map((task) => (
                <div key={task.id} className="tour-matura-task">
                  <strong>{task.text}</strong>
                  <div>
                    {task.options.map((option) => (
                      <button
                        key={option}
                        type="button"
                        className={answers[task.id] === option ? "btn-primary" : "btn-secondary"}
                        onClick={() => setAnswer(task.id, option)}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <>
            <span>{page.subtitle}</span>
            <h3>{score}/{allTasks.length} pkt - {percent}%</h3>
            <p>Najslabsze obszary demo: rownania, funkcja kwadratowa, zadania otwarte.</p>
            <div className="tour-progress-bar"><span style={{ width: `${percent}%` }} /></div>
            <button type="button" className="btn-primary" onClick={() => patch({ maturaChecked: true })}>
              Zapisz podejście demo lokalnie
            </button>
            {state.maturaChecked ? <p>Zapis matury został zachowany lokalnie w tej demonstracji.</p> : null}
          </>
        )}
      </div>
    </div>
  );
}

function MaturaRunnerPreview() {
  const examTasks = useMemo(() => generateOfficialAugust2025Matura(), []);
  const { pages } = useMemo(() => paginateExamTasks(examTasks), [examTasks]);
  const totalPoints = useMemo(
    () =>
      examTasks.reduce(
        (sum, item) =>
          sum +
          (typeof item?.meta?.maxPoints === "number" && item.meta.maxPoints > 0
            ? item.meta.maxPoints
            : 1),
        0
      ),
    [examTasks]
  );
  const [currentPage, setCurrentPage] = useState(0);
  const [pageDone, setPageDone] = useState(() => pages.map(() => false));
  const [answers, setAnswers] = useState(() => examTasks.map(() => ""));
  const [calcOpen, setCalcOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);

  const current = pages[currentPage] || { tasks: [], totalPoints: 0 };

  const setAnswer = (index, value) => {
    setAnswers((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const togglePageDone = (pageIndex) => {
    setPageDone((prev) => {
      const next = [...prev];
      next[pageIndex] = !next[pageIndex];
      return next;
    });
  };

  return (
    <div className="tour-matura-preview">
      <div className="tour-matura-runner-head">
        <div>
          <span>Arkusz maturalny</span>
          <strong>CKE 2025, poziom podstawowy</strong>
          <p>
            Pełny lokalny arkusz: {examTasks.length} zadań, {totalPoints} punktów,
            podział na strony i interaktywne odpowiedzi bez zapisu na serwerze.
          </p>
        </div>
        <div className="tour-matura-runner-tools">
          <button type="button" className="btn-secondary" onClick={() => setCalcOpen(true)}>
            Kalkulator
          </button>
          <button type="button" className="btn-secondary" onClick={() => setSheetOpen(true)}>
            Karta wzorów
          </button>
        </div>
      </div>

      <div className="tour-matura-runner-frame">
        <MaturaExamPager
          position="top"
          currentPage={currentPage}
          pages={pages}
          pageDone={pageDone}
          onChangePage={setCurrentPage}
          onTogglePageDone={togglePageDone}
          showTime={false}
          compact
        />

        <div className="tour-matura-runner-paper">
          {current.tasks.map((pt) => {
            const adapter = combinedRegistry[pt.task?.type] || combinedRegistry[pt.typeKey];

            if (!adapter) {
              return (
                <section key={pt.index} className="tour-matura-runner-task">
                  <h3>Zadanie {pt.meta?.taskNo || pt.index + 1}</h3>
                  <p>Brak adaptera dla tego typu zadania.</p>
                </section>
              );
            }

            return (
              <section key={pt.index} className="tour-matura-runner-task">
                <header>
                  <h3>Zadanie {pt.meta?.taskNo || pt.index + 1}</h3>
                  <span>{pt.points} pkt</span>
                </header>
                <div className="tour-matura-runner-task__body">
                  {adapter.renderTask(pt.task)}
                </div>
                {typeof adapter.renderInput === "function" ? (
                  <div className="tour-matura-runner-task__answer">
                    {adapter.renderInput(
                      pt.task,
                      answers[pt.index],
                      (value) => setAnswer(pt.index, value),
                      false
                    )}
                  </div>
                ) : null}
              </section>
            );
          })}
        </div>

        <MaturaExamPager
          position="bottom"
          currentPage={currentPage}
          pages={pages}
          pageDone={pageDone}
          onChangePage={setCurrentPage}
          onTogglePageDone={togglePageDone}
          showTime={false}
          compact
        />
      </div>

      <EdgeDrawer
        side="left"
        isOpen={calcOpen}
        onOpenChange={setCalcOpen}
        width={CALCULATOR_DRAWER_WIDTH}
        title="Kalkulator maturalny"
        showHandle={false}
        offsetTop={0}
        background="linear-gradient(180deg, rgba(255,153,0,0.36) 0%, rgba(255,190,70,0.36) 100%)"
        contentMaxWidth={380}
        zIndex={sheetOpen ? 10002 : 1000}
        showBackdrop={false}
        headerRight={
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setSheetOpen(true)}
            style={{ fontSize: 14, padding: "2px 10px", lineHeight: 1, borderRadius: 10 }}
            title="Otwórz kartę wzorów"
          >
            Karta wzorów
          </button>
        }
      >
        {calcOpen ? <ExamCalculator embedded /> : null}
      </EdgeDrawer>

      <FormulaSheet
        isOpen={sheetOpen}
        onOpenChange={setSheetOpen}
        width={FORMULA_SHEET_DRAWER_WIDTH}
        pdfPath={WZORY_URL}
        showBackdrop={false}
        headerRightExtra={
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setCalcOpen(true)}
            style={{ fontSize: 14, padding: "2px 10px", lineHeight: 1, borderRadius: 10 }}
            title="Otwórz kalkulator"
          >
            Kalkulator
          </button>
        }
      />
    </div>
  );
}

function SummaryPreview() {
  return (
    <div className="tour-summary-preview">
      <div className="tour-score-ring">
        <strong>5</strong>
        <span>modulow</span>
      </div>
      <div className="tour-summary-list">
        {[
          ["Lekcje", "Teoria, przykłady, ćwiczenia i quiz w jednym miejscu."],
          ["Tablica", "Prawdziwe narzędzie do pracy nad zadaniem, także w trybie demo."],
          ["Matura", "Osobny tryb arkusza z podsumowaniem i diagnoza."],
          ["Postep", "Historia wynikow lokalnie w demo, trwale po zalozeniu konta."],
        ].map(([title, body]) => (
          <div key={title}>
            <strong>{title}</strong>
            <p>{body}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function PlanPreview() {
  return (
    <div className="tour-plan-preview">
      {[
        ["Teraz", "Działające demo: lekcje, tablica, matura, quizy i postęp."],
        ["Najblizszy etap", "Samouczki, lepszy onboarding, testy z uczniami i rodzicami."],
        ["Skalowanie", "Pakiety dla maturzystów, korepetytorów, szkół i partnerów."],
        ["Metryki", "Retencja, wyniki, powroty do zadan, ukonczenia lekcji."],
      ].map(([title, body]) => (
        <div key={title}>
          <strong>{title}</strong>
          <p>{body}</p>
        </div>
      ))}
    </div>
  );
}

function Preview({ stepId, state, patch }) {
  if (stepId === "intro") return <IntroPreview />;
  if (stepId === "board") return <BoardPreview />;
  if (stepId === "lesson") return <LessonPreview state={state} patch={patch} />;
  if (stepId === "matura") return <MaturaRunnerPreview />;
  if (stepId === "summary") return <SummaryPreview />;
  return <PlanPreview />;
}

export default function GrantInvestorTourPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [state, patch] = useTourStorage();
  const isInvestorView = useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    return params.get("audience") === "investor" || params.get("investor") === "1";
  }, [location.search]);
  const steps = useMemo(
    () => (isInvestorView ? [...publicSteps, investorStep] : publicSteps),
    [isInvestorView]
  );
  const [activeIndex, setActiveIndex] = useState(() => getStepFromSearch(location.search, steps));

  useEffect(() => {
    setActiveIndex(getStepFromSearch(location.search, steps));
  }, [location.search, steps]);

  const activeStep = steps[activeIndex] || steps[0];
  const progress = Math.round(((activeIndex + 1) / steps.length) * 100);

  const goToStep = (indexOrId) => {
    const index = typeof indexOrId === "string"
      ? steps.findIndex((step) => step.id === indexOrId)
      : Number(indexOrId);
    const bounded = (index + steps.length) % steps.length;
    const params = new URLSearchParams(location.search || "");
    params.set("step", steps[bounded].id);
    navigate({ pathname: location.pathname, search: `?${params.toString()}` }, { replace: false });
  };

  return (
    <div className="product-tour-page">
      <section className="product-tour-panel">
        <aside className="product-tour-sidebar">
          <div>
            <div className="product-tour-kicker">Demonstracja Matematronu</div>
            <p>
              Wybierz krok i zobacz najważniejsze moduły platformy bez zakładania konta.
            </p>
          </div>

          <nav className="product-tour-steps" aria-label="Kroki demonstracji">
            {steps.map((step, index) => (
              <button
                key={step.id}
                type="button"
                className={index === activeIndex ? "btn-primary is-active" : "btn-secondary"}
                onClick={() => goToStep(index)}
              >
                <span>{String(index + 1).padStart(2, "0")}</span>
                {step.label}
              </button>
            ))}
          </nav>

          <div className="product-tour-sidebar__bottom">
            <div className="product-tour-progress" aria-label={`Postęp demonstracji ${progress}%`}>
              <span style={{ width: `${progress}%` }} />
            </div>
            <div className="product-tour-actions">
              <button type="button" className="btn-secondary" onClick={() => goToStep(activeIndex - 1)}>Wstecz</button>
              <button type="button" className="btn-primary" onClick={() => goToStep(activeIndex + 1)}>Dalej</button>
            </div>
          </div>
        </aside>

        <main className="product-tour-preview" aria-live="polite">
          <div className="product-tour-preview__header">
            <span>{String(activeIndex + 1).padStart(2, "0")} / {activeStep.label}</span>
            <strong>{isInvestorView ? "wariant inwestorski" : "wariant publiczny"}</strong>
          </div>
          <Preview stepId={activeStep.id} state={state} patch={patch} />
        </main>
      </section>
    </div>
  );
}
